import { useEffect } from "react";

import ModalCloseButton from "@/components/base/ModalCloseButton";

export interface SourceMemberRow {
  email: string;
  displayName: string;
  roles: string;
  connections?: string[];
}

interface SourceMembersModalProps {
  ariaLabel: string;
  contextLabel: string;
  members: SourceMemberRow[];
  loading?: boolean;
  loadError?: string;
  showConnection?: boolean;
  showRoles?: boolean;
  onRetry?: () => void;
  onClose: () => void;
}

export default function SourceMembersModal({
  ariaLabel,
  contextLabel,
  members,
  loading = false,
  loadError = "",
  showConnection = false,
  showRoles = true,
  onRetry,
  onClose,
}: SourceMembersModalProps) {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  useEffect(() => {
    const scrollContainers = [
      document.documentElement,
      document.body,
      document.querySelector<HTMLElement>(".admin-layout > main"),
    ].filter((element): element is HTMLElement => element !== null);
    const previousOverflow = scrollContainers.map((element) => element.style.overflow);
    scrollContainers.forEach((element) => {
      element.style.overflow = "hidden";
    });

    return () => {
      scrollContainers.forEach((element, index) => {
        element.style.overflow = previousOverflow[index];
      });
    };
  }, []);

  return (
    <div
      data-testid="source-members-modal-overlay"
      className="fixed inset-0 z-[9999] flex items-center justify-center px-4"
      style={{ backgroundColor: "rgba(0,0,0,0.35)" }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        className="flex max-h-[76vh] w-[920px] max-w-full flex-col overflow-hidden rounded-xl bg-white"
        style={{ boxShadow: "0 8px 32px rgba(0,0,0,0.14)" }}
      >
        <div className="flex items-start gap-3 border-b border-gray-100 px-5 py-4">
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold text-gray-900">Source Members</h3>
            <p className="mt-1 truncate text-xs text-gray-400">
              {contextLabel}
              {!loading && !loadError ? ` · ${members.length} members with email` : ""}
            </p>
          </div>
          <ModalCloseButton ariaLabel="Close Source Members" onClick={onClose} />
        </div>

        <div
          data-testid="source-members-scroll-region"
          className="min-h-0 flex-1 overflow-auto overscroll-contain p-5"
        >
          {loading ? (
            <div className="px-6 py-16 text-center text-sm text-gray-400">
              <i className="ri-loader-4-line mb-2 inline-block animate-spin text-2xl" />
              <div>Loading Source members...</div>
            </div>
          ) : loadError ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <i className="ri-error-warning-line mb-3 text-3xl text-red-300" />
              <p className="text-sm font-medium text-red-500">{loadError}</p>
              {onRetry && (
                <button
                  type="button"
                  onClick={onRetry}
                  className="mt-3 rounded-lg px-3 py-1.5 text-xs font-medium text-[#830051] transition-colors hover:bg-gray-50"
                >
                  Retry
                </button>
              )}
            </div>
          ) : members.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <i className="ri-group-line mb-3 text-3xl text-gray-300" />
              <p className="text-sm font-medium text-gray-400">No members with email</p>
              <p className="mt-1 text-xs text-gray-300">
                No members with a non-empty Email are available.
              </p>
            </div>
          ) : (
            <div className="overflow-hidden rounded-xl border border-gray-100 bg-white">
              <table className="w-full table-fixed font-sans text-sm">
                <colgroup>
                  {showConnection && showRoles ? (
                    <>
                      <col className="w-[40%]" />
                      <col className="w-[22%]" />
                      <col className="w-[16%]" />
                      <col className="w-[22%]" />
                    </>
                  ) : showConnection ? (
                    <>
                      <col className="w-[45%]" />
                      <col className="w-[30%]" />
                      <col className="w-[25%]" />
                    </>
                  ) : showRoles ? (
                    <>
                      <col className="w-[50%]" />
                      <col className="w-[30%]" />
                      <col className="w-[20%]" />
                    </>
                  ) : (
                    <>
                      <col className="w-[60%]" />
                      <col className="w-[40%]" />
                    </>
                  )}
                </colgroup>
                <thead className="bg-gray-50">
                  <tr>
                    {[
                      "Email",
                      "Display Name",
                      ...(showRoles ? ["Roles"] : []),
                      ...(showConnection ? ["Connection"] : []),
                    ].map((heading) => (
                      <th
                        key={heading}
                        className="border-b border-gray-100 px-4 py-3 text-left text-xs font-semibold text-gray-500"
                      >
                        {heading}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {members.map((member, index) => {
                    const roles = member.roles
                      .split(";")
                      .map((role) => role.trim())
                      .filter(Boolean);
                    const connections = Array.from(new Set(member.connections ?? []));
                    return (
                      <tr
                        key={`${member.email}-${index}`}
                        className="border-b border-gray-50 transition-colors last:border-b-0 hover:bg-gray-50/60"
                      >
                        <td className="break-all px-4 py-3 text-xs text-gray-700">
                          {member.email}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {member.displayName || "-"}
                        </td>
                        {showRoles && (
                          <td className="px-4 py-3">
                            {roles.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {roles.map((role) => (
                                  <span
                                    key={role}
                                    className="inline-flex rounded bg-gray-100 px-1.5 py-0.5 text-[11px] text-gray-600"
                                  >
                                    {role}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="text-xs text-gray-300">-</span>
                            )}
                          </td>
                        )}
                        {showConnection && (
                          <td className="px-4 py-3">
                            {connections.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {connections.map((connection) => (
                                  <span
                                    key={connection}
                                    className="inline-flex whitespace-nowrap rounded-md border border-gray-100 bg-gray-50/70 px-2 py-1 text-[11px] text-gray-600"
                                  >
                                    {connection}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="text-xs text-gray-300">-</span>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
